import { useState, useContext, useMemo, useEffect } from 'react'
import { Typography, TextField, Box, Tabs, Tab, Button, Stack, Switch } from '@mui/material'

import moment from 'moment-timezone'
import { enqueueSnackbar } from 'notistack'

import WarningAmberIcon from '@mui/icons-material/WarningAmber'

import { AppContext } from '/providers/app-context'
import { toggleScannerPassEnabled, changeStampcardStamps } from '/providers/studio-api'
import StyledPaper from '/components/paper'

const PassEditorStamp = ({ pass, template, setPass }) => {

  const [tabIndex, setTabIndex] = useState("add")

  return (
    <div className='h-full pb-32'>
      <StyledPaper>
        <Typography variant="h6" sx={{ p: 2 }}>Stampcard Editor</Typography>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: 1 }}>
          <Tabs value={tabIndex} onChange={(e, value) => setTabIndex(value)}>
            <Tab label="Add Stamps" value="add" />
            <Tab label="Remove stamps" value="remove" />
            <Tab label="Reset" value="reset" />
            <Tab label="Manage" value="manage" />
          </Tabs>
        </Box>
      </StyledPaper>

      <div className='overflow-y-scroll flex flex-col h-full pb-32 items-center justify-around'>
        <StyledPaper>
          <div className='p-5'>
          <AddTab tabIndex={tabIndex} index="add" pass={pass} template={template} setPass={setPass} />
          <RemoveTab tabIndex={tabIndex} index="remove" pass={pass} template={template} setPass={setPass} />
          
          <ResetTab tabIndex={tabIndex} index="reset" pass={pass} template={template} setPass={setPass} />
          <ManageTab tabIndex={tabIndex} index="manage" pass={pass} templateId={template.id} setPass={setPass} />
          </div>
        </StyledPaper>
      </div>
    </div>
  )
}

export default PassEditorStamp

const AddTab = ({ tabIndex, index, pass, template, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")

  const save = async () => {
    setProcessing(true)
    const result = await changeStampcardStamps({ apiSource, apiKey, templateId: template.id, passId: pass.pass_id, action: "add", amount: parseFloat(amount), note })
    setProcessing(false)

    if (result.error) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setAmount("")
      setNote("")
      setPass(result)
      enqueueSnackbar({ message: 'Stamps added', variant: 'success' })
    }
  }

  if (tabIndex === index) return <NumericInput template={template} pass={pass} note={note} setNote={setNote} amount={amount} setAmount={setAmount} label="Number of stamps to add" helperText={`We will calculate any filled cards automatically (${template.scanner_data.stamps_per_card} stamps per card)`} submitText="Add" save={save} />
}

const RemoveTab = ({ tabIndex, index, pass, template, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")

  const save = async () => {
    setProcessing(true)
    const result = await changeStampcardStamps({ apiSource, apiKey, templateId: template.id, passId: pass.pass_id, action: "remove", amount: parseFloat(amount), note })
    setProcessing(false)

    if (result.error) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setAmount("")
      setNote("")
      setPass(result)
      enqueueSnackbar({ message: 'Stamps removed', variant: 'success' })
    }
  }

  if (tabIndex === index) return <NumericInput template={template} pass={pass} note={note} setNote={setNote} amount={amount} setAmount={setAmount} label="Number of stamps to remove" helperText={`We will calculate any full cards to remove automatically (${template.scanner_data.stamps_per_card} stamps per card)`} submitText="Remove" save={save} limitMax={true} />
}


const ResetTab = ({ tabIndex, index, pass, template, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)
  const [amount, setAmount] = useState("")
  const [note, setNote] = useState("")

  const save = async () => {
    setProcessing(true)
    const result = await changeStampcardStamps({ apiSource, apiKey, templateId: template.id, passId: pass.pass_id, action: "reset", amount: parseFloat(amount), note })
    setProcessing(false)

    if (result.error) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setAmount("")
      setNote("")
      setPass(result)
      enqueueSnackbar({ message: 'Stamps reset to zero', variant: 'success' })
    }
  }

  const num_stamps = pass.scanner_instance_data["__*_current_stamps__"] + (pass.scanner_instance_data["__*_current_full_cards__"] * template.scanner_data.stamps_per_card)
  const disabled = pass.scanner_instance_data["*_disabled"] !== null

  if (tabIndex === index) return (
    <div className='flex flex-1 flex-col justify-center items-center gap-10'>
      <Typography variant='h4'>This action will remove all collected stamps / full cards</Typography>

      {!disabled &&
        <div className='flex flex-col justify-around items-center gap-10'>
          <div>
            <div className='font-semibold text-xl self-start text-gray-400'>Current stamps : {num_stamps}</div>
            <div className='font-semibold self-start text-gray-400'>Full cards : {pass.scanner_instance_data["__*_current_full_cards__"]}, Stamps : {pass.scanner_instance_data["__*_current_stamps__"]}</div>
          </div>

          <TextField multiline fullWidth rows={4} label="Note" value={note} onChange={e => setNote(e.target.value)} helperText={`Add a note to explain why you are resetting stamps to zero.`} slotProps={{
            inputLabel: {
              shrink: true,
            }
          }}>{note}</TextField>

          <div className='self-end'><Button variant='contained' disabled={note === ""} onClick={save}>Reset</Button></div>
        </div>
      }

      {disabled &&
        <div><WarningAmberIcon className='mr-5 text-red-500' />
          Stampcard is disabled and cannot be used
          <br />
          <span className='text-gray-500'>You can change this in the MANAGE section</span>
        </div>
      }

    </div>
  )
}

const regex = /^[0-9]+$/i

const NumericInput = ({ template, pass, amount, setAmount, note, setNote, label, helperText, submitText, save, limitMax }) => {

  const disabled = pass.scanner_instance_data["*_disabled"] !== null
  const num_stamps = pass.scanner_instance_data["__*_current_stamps__"] + (pass.scanner_instance_data["__*_current_full_cards__"] * template.scanner_data.stamps_per_card)

  return (
    <div className='flex flex-1 flex-col justify-around items-center'>
      <div>
        <div className='flex flex-col justify-around items-center gap-10'>
          <div>
            <div className='font-semibold text-xl self-start text-gray-400'>Current stamps : {num_stamps}</div>
            <div className='font-semibold self-start text-gray-400'>Full cards : {pass.scanner_instance_data["__*_current_full_cards__"]}, Stamps : {pass.scanner_instance_data["__*_current_stamps__"]}</div>
          </div>

          {!disabled &&
            <>

              <TextField
                value={amount}
                onChange={e => {
                  if (e.target.value === '' || regex.test(e.target.value)) {
                    setAmount(e.target.value)
                  }
                }}
                variant='outlined'
                label={label}
                placeholder='0'
                slotProps={{
                  input: {
                    style: { fontSize: 50, width: 300 }
                  },
                  inputLabel: {
                    shrink: true,
                  }
                }}
              />

              <div className='font-semibold text-xl self-start'>{helperText}</div>

              <TextField multiline fullWidth rows={4} label="note" value={note} onChange={e => setNote(e.target.value)} helperText={`Add a note to explain why you are adjusting the stamp count.`} slotProps={{
                inputLabel: {
                  shrink: true,
                }
              }}>{note}</TextField>

              <div className='self-end'><Button variant='contained' disabled={note === "" || amount === "" || (limitMax && amount > num_stamps)} onClick={save}>{submitText}</Button></div>

            </>
          }

          {disabled &&
            <div><WarningAmberIcon className='mr-5 text-red-500' />
              Stampcard is disabled and cannot be used
              <br />
              <span className='text-gray-500'>You can change this in the MANAGE section</span>
            </div>
          }

        </div>
      </div>
    </div>
  )
}


const ManageTab = ({ tabIndex, index, pass, templateId, setPass }) => {

  const { apiSource, apiKey, setProcessing } = useContext(AppContext)

  const [enabled, setEnabled] = useState(pass.scanner_instance_data["*_disabled"] === null)
  const [note, setNote] = useState("")

  const passCurrentlyEnabled = useMemo(() => pass.scanner_instance_data["*_disabled"] === null, [pass.scanner_instance_data["*_disabled"]])

  const noteDisabled = useMemo(() => enabled === passCurrentlyEnabled, [enabled, passCurrentlyEnabled])

  const saveEnabledDisabled = async () => {
    setProcessing(true)
    const result = await toggleScannerPassEnabled({ apiSource, apiKey, templateId, passId: pass.pass_id, enabled, note })
    setProcessing(false)

    if (result.error) {
      enqueueSnackbar({ message: result.error, variant: 'error' })
    } else {
      setPass(result)
      setNote("")
      enqueueSnackbar({ message: 'Stampcard  ' + (enabled ? "enabled" : "disabled"), variant: 'success' })
    }
  }

  if (tabIndex === index) return (
    <div className='flex flex-1 flex-col justify-around items-center'>
      <div>
        <div className='flex flex-col justify-around items-center gap-10'>

          <div style={{ width: 400 }} className='flex flex-col gap-5'>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography>Stampcard status: Disabled</Typography>
              <Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />
              <Typography>Enabled</Typography>
            </Stack>

            <TextField multiline fullWidth rows={4} label="Note" onChange={e => setNote(e.target.value)} disabled={noteDisabled} helperText={`Add a note to explain why you are ${passCurrentlyEnabled ? "disabling" : "enabling"} this stampcard `} slotProps={{
              inputLabel: {
                shrink: true,
              }
            }}>{note}</TextField>
            <div className='self-end'>
              <Button variant='contained' disabled={note === ""} onClick={saveEnabledDisabled}>Save</Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}


const formatDate = date => moment(date, "YYYYMMDDHHmmSS").format("DD MMM YYYY HH:mm")